import Archive from "../../Archive";
import "../../Home.css";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navigation from "../../Navigation";
import Footer from "../../Footer";
import { useLanguage } from "../../../context/LanguageContext";

const DhowPage = () => {
  const { language } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scrollTop, setScrollTop] = useState(0);
  const sectionRefs = useRef([]);
  const navigate = useNavigate();

  // Initialize section refs
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, 4); // We have 4 sections (reduced from 5)
  }, []);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrollTop(window.scrollY);

      // Determine active section based on scroll position
      const currentPosition = window.pageYOffset + 300;
      let active = 0;

      sectionRefs.current.forEach((ref, index) => {
        if (ref && ref.offsetTop <= currentPosition) {
          active = index;
        }
      });

      if (active !== activeSection) {
        setActiveSection(active);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeSection]);

  // Fade in effect on page load
  useEffect(() => {
    setLoading(false);
  }, []);

  // Translations
  const translations = {
    backToProjects: {
      en: "Back to Projects",
      zh: "返回项目列表",
      ja: "プロジェクトに戻る",
    },
    title: {
      en: "Dhow.cn: Art Blockchain Platform",
      zh: "Dhow.cn: 艺术品区块链平台",
      ja: "Dhow.cn: アートブロックチェーンプラットフォーム",
    },
    tagline: {
      en: "Connecting Chinese artists with global collectors through Web3",
      zh: "通过Web3技术连接中国艺术家与全球收藏家",
      ja: "Web3を通じて中国人アーティストと世界中のコレクターをつなぐ",
    },
    overview: {
      en: "Project Overview",
      zh: "项目概述",
      ja: "プロジェクト概要",
    },
    description: {
      en: "Dhow.cn was a blockchain platform connecting Chinese artists with global collectors. The platform aimed to enhance art trading transparency and trust through Web3 technology, but was not officially launched.",
      zh: "Dhow.cn是一个区块链平台，连接中国艺术家与全球收藏家，通过Web3技术提升艺术品交易透明度。虽未正式上线，但积累了宝贵经验。",
      ja: "Dhow.cnは中国人アーティストと世界中のコレクターをつなぐブロックチェーンプラットフォームでした。Web3技術を通じてアート取引の透明性と信頼性を高めることを目指しましたが、正式にローンチされませんでした。",
    },
    challenge: {
      en: "The Challenge",
      zh: "项目挑战",
      ja: "課題",
    },
    challengeText: {
      en: "Limited artist matching efficiency, cultural barriers with international art markets, and resource constraints in market promotion.",
      zh: "艺术家匹配效率有限，国际艺术市场文化障碍，市场推广资源受限。",
      ja: "アーティストマッチングの効率制限、国際芸術市場の文化障壁、市場プロモーションのリソース制約。",
    },
    solution: {
      en: "Our Approach",
      zh: "解决方案",
      ja: "アプローチ",
    },
    solutionText: {
      en: "Vue.js frontend, Django backend with MySQL database. Integration with China's copyright chain for artwork authentication. Artists collaborate on platform with physical artworks consigned to central warehouse.",
      zh: "Vue.js前端，Django后端配合MySQL数据库。与中国版权链集成用于艺术品认证。艺术家在平台上协作，实物艺术品寄售至中心仓库。",
      ja: "Vue.jsフロントエンド、MySQLデータベースを備えたDjangoバックエンド。中国の著作権チェーンと統合してアート認証。アーティストはプラットフォーム上でコラボレーションし、実物の作品は中央倉庫に寄託。",
    },
    features: {
      en: "Platform Structure",
      zh: "平台架构",
      ja: "プラットフォーム構造",
    },
    artCuration: {
      en: "Artist Collaboration",
      zh: "艺术家协作",
      ja: "アーティストコラボレーション",
    },
    artCurationText: {
      en: "Platform for Chinese artists to chat and collaborate on creative works together",
      zh: "为中国艺术家提供聊天和共同创作的平台",
      ja: "中国人アーティストがチャットをして共同で創作作品を制作するためのプラットフォーム",
    },
    artistProfiles: {
      en: "Blockchain Integration",
      zh: "区块链集成",
      ja: "ブロックチェーン統合",
    },
    artistProfilesText: {
      en: "Integration with a national-level copyright blockchain for artwork authentication and provenance tracking",
      zh: "与国家级版权区块链集成，用于艺术品认证和来源追踪",
      ja: "アート作品の認証と出所追跡のための国家レベルの著作権ブロックチェーンとの統合",
    },
    secure: {
      en: "Physical Consignment",
      zh: "实物寄售",
      ja: "実物委託販売",
    },
    secureText: {
      en: "Central warehouse system for physical artwork storage and verification before overseas shipping",
      zh: "中心仓库系统，用于海外运输前的实物艺术品存储和验证",
      ja: "海外発送前の実物アート作品の保管と検証のための中央倉庫システム",
    },
    provenance: {
      en: "International Market",
      zh: "国际市场",
      ja: "国際マーケット",
    },
    provenanceText: {
      en: "Connections with New York galleries and collectors to facilitate cross-border art transactions",
      zh: "与纽约画廊和收藏家的联系，促进跨境艺术交易",
      ja: "国境を越えたアート取引を促進するためのニューヨークのギャラリーやコレクターとのつながり",
    },
    logistics: {
      en: "Documentation",
      zh: "文档记录",
      ja: "文書記録",
    },
    logisticsText: {
      en: "Detailed multilingual documentation of artwork specifications, artist information, and authentication certificates",
      zh: "艺术品规格、艺术家信息和认证证书的详细多语言文档",
      ja: "アート作品の仕様、アーティスト情報、認証証明書の詳細な多言語文書",
    },
    techStack: {
      en: "Technology Stack",
      zh: "技术栈",
      ja: "技術スタック",
    },
    impact: {
      en: "Lessons & Experience",
      zh: "经验与教训",
      ja: "教訓と経験",
    },
    impactMetrics: {
      en: [
        'Preserved "Yanking Bajue" intangible cultural heritage artworks from Lang Fang',
        "Established a subsidiary business unit for art consignment and export",
        "Gained valuable experience in cross-cultural art market development",
        "Built connections with international galleries and collectors",
      ],
      zh: [
        '保留了郎坊提供的"燕京八绝"类非遗作品',
        '建立了"艺术品寄售与出口"的子业务单元',
        "积累了跨文化艺术市场开发的宝贵经验",
        "建立了与国际画廊和收藏家的联系",
      ],
      ja: [
        "ラン・ファンから提供された「燕京八絶」無形文化遺産アート作品を保存",
        "アート委託販売と輸出のための子会社事業ユニットを設立",
        "異文化間アート市場開発における貴重な経験を獲得",
        "国際的なギャラリーやコレクターとのつながりを構築",
      ],
    },
    conclusion: {
      en: "Conclusion",
      zh: "结论",
      ja: "結論",
    },
    conclusionText: {
      en: "While Dhow.cn did not officially launch, the project provided valuable insights into the challenges of developing a blockchain platform for international art trading. The team's experience in cross-cultural communication, understanding of global art markets, and technical implementation of blockchain solutions remain valuable assets. The project's focus on using technology to enhance transparency and trust in art transactions continues as a subsidiary business unit specializing in art consignment and export.",
      zh: "尽管Dhow.cn未能正式上线，但该项目为国际艺术品交易区块链平台的开发挑战提供了宝贵的见解。团队在跨文化沟通、理解全球艺术市场和区块链解决方案的技术实施方面积累的经验仍然是宝贵的财富。项目致力于通过技术提高艺术品交易的透明度和信任度的理念，继续以专注于艺术品寄售和出口的子业务单元形式延续。",
      ja: "Dhow.cnは正式に立ち上がりませんでしたが、このプロジェクトは国際的なアート取引のためのブロックチェーンプラットフォーム開発の課題に関する貴重な洞察を提供しました。チームの異文化間コミュニケーション、グローバルアート市場の理解、ブロックチェーンソリューションの技術的実装における経験は、依然として貴重な資産です。アート取引の透明性と信頼性を高めるためにテクノロジーを活用するというプロジェクトの焦点は、アート委託販売と輸出を専門とする子会社事業ユニットとして継続しています。",
    },
  };

  // Technology stack
  const techStack = [
    { name: "Vue.js", category: "frontend", color: "blue" },
    { name: "Django", category: "backend", color: "green" },
    { name: "MySQL", category: "database", color: "green" },
    { name: "SQLite", category: "database", color: "orange" },
    {
      name: "National Copyright Chain",
      category: "blockchain",
      color: "purple",
    },
    { name: "WeChat Mini App", category: "mobile", color: "indigo" },
    { name: "i18n", category: "localization", color: "teal" },
    { name: "Tailwind CSS", category: "frontend", color: "blue" },
    { name: "Aliyun", category: "cloud", color: "cyan" },
  ];

  // Function to get tag color based on category
  const getTagColor = (category) => {
    switch (category) {
      case "frontend":
        return "bg-blue-100 text-blue-800";
      case "backend":
        return "bg-green-100 text-green-800";
      case "database":
        return "bg-green-100 text-green-800";
      case "cloud":
        return "bg-orange-100 text-orange-800";
      case "blockchain":
        return "bg-purple-100 text-purple-800";
      case "payment":
        return "bg-indigo-100 text-indigo-800";
      case "localization":
        return "bg-teal-100 text-teal-800";
      case "devops":
        return "bg-cyan-100 text-cyan-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Scroll to section function
  const scrollToSection = (index) => {
    if (sectionRefs.current[index]) {
      sectionRefs.current[index].scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-neutral-50">
      <Navigation activeSection="projects" />

      {/* Side Navigation Dots */}
      <div className="fixed right-8 top-1/2 transform -translate-y-1/2 z-20 hidden md:block">
        <div className="flex flex-col space-y-3">
          {Array(4)
            .fill(0)
            .map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToSection(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  activeSection === index
                    ? "bg-primary-500 scale-125"
                    : "bg-neutral-300 hover:bg-neutral-400"
                }`}
                aria-label={`Go to section ${index + 1}`}
              />
            ))}
        </div>
      </div>

      <div className="pt-24 pb-12">
        {/* Back to Projects Link */}
        <div className="container mx-auto max-w-7xl px-6 mb-6">
          <Link
            to="/projects"
            className="text-primary-500 hover:text-primary-600 transition-colors flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clipRule="evenodd"
              />
            </svg>
            {translations.backToProjects[language] ||
              translations.backToProjects.en}
          </Link>
        </div>

        {/* Hero Section */}
        <section
          ref={(el) => (sectionRefs.current[0] = el)}
          className={`relative py-16 px-6 transition-opacity duration-1000 ${
            loading ? "opacity-0" : "opacity-100"
          }`}
        >
          <div className="container mx-auto max-w-7xl">
            <div className="flex flex-col lg:flex-row gap-12">
              <div className="lg:w-1/2 order-2 lg:order-1">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-neutral-800 leading-tight">
                  {translations.title[language] || translations.title.en}
                </h1>
                <p className="text-xl text-neutral-600 mb-8 max-w-xl">
                  {translations.tagline[language] || translations.tagline.en}
                </p>

                {/* Tech Tags */}
                <div className="flex flex-wrap gap-2 mb-8">
                  {techStack.slice(0, 5).map((tech, i) => (
                    <span
                      key={i}
                      className={`${getTagColor(
                        tech.category
                      )} text-xs font-medium px-3 py-1 rounded-full`}
                    >
                      {tech.name}
                    </span>
                  ))}
                </div>

                <div className="mt-10">
                  <a
                    href="https://www.dhow.cn"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-primary-500 text-white px-8 py-3 rounded-full hover:bg-primary-600 transition-all duration-300 shadow-sm hover:shadow-md mr-4"
                  >
                    {language === "en"
                      ? "Visit Website"
                      : language === "zh"
                      ? "访问网站"
                      : "ウェブサイトを訪問"}
                  </a>
                  <button
                    onClick={() => scrollToSection(1)}
                    className="border border-neutral-200 bg-white px-8 py-3 rounded-full hover:border-primary-300 transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    {language === "en"
                      ? "Learn More"
                      : language === "zh"
                      ? "了解更多"
                      : "詳細を見る"}
                  </button>
                </div>
              </div>

              <div className="lg:w-1/2 order-1 lg:order-2">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="/assets/projects/dhow/community.png"
                    alt="DHOW Platform Preview"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary-900/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Background elements */}
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-primary-100 rounded-full filter blur-3xl opacity-50 z-0"></div>
          <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-indigo-100 rounded-full filter blur-3xl opacity-30 z-0"></div>
        </section>

        {/* Overview Section */}
        <section
          ref={(el) => (sectionRefs.current[1] = el)}
          className="py-20 px-6 bg-white"
        >
          <div className="container mx-auto max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-light mb-16 text-center text-neutral-800">
              {translations.overview[language] || translations.overview.en}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              <div className="bg-neutral-50 p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                <h3 className="text-2xl font-light mb-4 text-neutral-800 text-left">
                  {translations.challenge[language] ||
                    translations.challenge.en}
                </h3>
                <p className="text-neutral-600 text-left">
                  {translations.challengeText[language] ||
                    translations.challengeText.en}
                </p>
              </div>

              <div className="bg-primary-50 p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300">
                <h3 className="text-2xl font-light mb-4 text-neutral-800 text-left">
                  {translations.solution[language] || translations.solution.en}
                </h3>
                <p className="text-neutral-600 text-left">
                  {translations.solutionText[language] ||
                    translations.solutionText.en}
                </p>
              </div>

              <div className="bg-neutral-50 p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 md:col-span-2 lg:col-span-1">
                <h3 className="text-2xl font-light mb-4 text-neutral-800 text-left">
                  {translations.impact[language] || translations.impact.en}
                </h3>
                <ul className="list-disc pl-5 text-neutral-600 space-y-2 text-left">
                  {(
                    translations.impactMetrics[language] ||
                    translations.impactMetrics.en
                  ).map((metric, index) => (
                    <li key={index} className="text-left">
                      {metric}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Features Grid */}
            <h3 className="text-2xl font-light mb-8 text-center text-neutral-800">
              {translations.features[language] || translations.features.en}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-primary-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2">
                  {translations.artCuration[language] ||
                    translations.artCuration.en}
                </h4>
                <p className="text-neutral-600 text-sm">
                  {translations.artCurationText[language] ||
                    translations.artCurationText.en}
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-indigo-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2">
                  {translations.artistProfiles[language] ||
                    translations.artistProfiles.en}
                </h4>
                <p className="text-neutral-600 text-sm">
                  {translations.artistProfilesText[language] ||
                    translations.artistProfilesText.en}
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2">
                  {translations.secure[language] || translations.secure.en}
                </h4>
                <p className="text-neutral-600 text-sm">
                  {translations.secureText[language] ||
                    translations.secureText.en}
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-purple-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2">
                  {translations.provenance[language] ||
                    translations.provenance.en}
                </h4>
                <p className="text-neutral-600 text-sm">
                  {translations.provenanceText[language] ||
                    translations.provenanceText.en}
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-rose-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2">
                  {translations.logistics[language] ||
                    translations.logistics.en}
                </h4>
                <p className="text-neutral-600 text-sm">
                  {translations.logisticsText[language] ||
                    translations.logisticsText.en}
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-amber-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                    />
                  </svg>
                </div>
                <h4 className="text-xl font-medium mb-2">
                  {language === "en"
                    ? "Price Transparency"
                    : language === "zh"
                    ? "价格透明"
                    : "価格の透明性"}
                </h4>
                <p className="text-neutral-600 text-sm">
                  {language === "en"
                    ? "Clear pricing structure with no hidden fees and fair commission rates for artists"
                    : language === "zh"
                    ? "清晰的价格结构，没有隐藏费用，为艺术家提供公平的佣金比例"
                    : "隠れた料金がなく、アーティストに公正な手数料率を提供する明確な価格構造"}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Stack Section */}
        <section
          ref={(el) => (sectionRefs.current[2] = el)}
          className="py-20 px-6 bg-white"
        >
          <div className="container mx-auto max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-light mb-12 text-center text-neutral-800">
              {translations.techStack[language] || translations.techStack.en}
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {techStack.map((tech, index) => (
                <div
                  key={index}
                  className={`flex flex-col items-center justify-center p-6 rounded-xl ${getTagColor(
                    tech.category
                  )} bg-opacity-20 hover:bg-opacity-30 transition-all duration-300`}
                >
                  <span className="text-lg font-medium mb-2">{tech.name}</span>
                  <span className="text-xs opacity-70 capitalize">
                    {tech.category}
                  </span>
                </div>
              ))}
            </div>

            {/* Platform architecture visualization */}
            <div className="mt-16 p-8 bg-neutral-50 rounded-2xl">
              <h3 className="text-2xl font-light mb-8 text-center text-neutral-800">
                {language === "en"
                  ? "Platform Architecture"
                  : language === "zh"
                  ? "平台架构"
                  : "プラットフォームアーキテクチャ"}
              </h3>

              <div className="relative max-w-4xl mx-auto">
                {/* Architecture Diagram */}
                <div className="flex flex-col items-center space-y-6">
                  {/* User Layer */}
                  <div className="flex justify-center items-center w-full">
                    <div className="flex space-x-6">
                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 text-center">
                        <div className="text-blue-600">
                          {language === "en"
                            ? "Artists"
                            : language === "zh"
                            ? "艺术家"
                            : "アーティスト"}
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 text-center">
                        <div className="text-blue-600">
                          {language === "en"
                            ? "Collectors"
                            : language === "zh"
                            ? "收藏家"
                            : "コレクター"}
                        </div>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 text-center">
                        <div className="text-blue-600">
                          {language === "en"
                            ? "Galleries"
                            : language === "zh"
                            ? "画廊"
                            : "ギャラリー"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Connection Line */}
                  <div className="w-0.5 h-6 bg-neutral-300"></div>

                  {/* Frontend Layer */}
                  <div className="bg-indigo-50 p-4 w-full rounded-xl border border-indigo-100 text-center">
                    <div className="text-indigo-600">
                      {language === "en"
                        ? "Frontend (React.js, TailwindCSS, i18next)"
                        : language === "zh"
                        ? "前端 (React.js, TailwindCSS, i18next)"
                        : "フロントエンド (React.js, TailwindCSS, i18next)"}
                    </div>
                  </div>

                  {/* Connection Line */}
                  <div className="w-0.5 h-6 bg-neutral-300"></div>

                  {/* API Layer */}
                  <div className="bg-green-50 p-4 w-full rounded-xl border border-green-100 text-center">
                    <div className="text-green-600">
                      {language === "en"
                        ? "API Layer (Node.js, Express)"
                        : language === "zh"
                        ? "API层 (Node.js, Express)"
                        : "APIレイヤー (Node.js, Express)"}
                    </div>
                  </div>

                  {/* Connection Lines */}
                  <div className="flex justify-center w-full">
                    <div className="grid grid-cols-3 gap-6 w-full">
                      <div className="flex flex-col items-center">
                        <div className="w-0.5 h-6 bg-neutral-300"></div>
                        <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 text-center w-full">
                          <div className="text-purple-600">
                            {language === "en"
                              ? "Authentication"
                              : language === "zh"
                              ? "认证服务"
                              : "認証"}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-0.5 h-6 bg-neutral-300"></div>
                        <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 text-center w-full">
                          <div className="text-purple-600">
                            {language === "en"
                              ? "Art Management"
                              : language === "zh"
                              ? "艺术品管理"
                              : "アート管理"}
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="w-0.5 h-6 bg-neutral-300"></div>
                        <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 text-center w-full">
                          <div className="text-purple-600">
                            {language === "en"
                              ? "Payment Processing"
                              : language === "zh"
                              ? "支付处理"
                              : "決済処理"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Connection Line */}
                  <div className="w-0.5 h-6 bg-neutral-300"></div>

                  {/* Database Layer */}
                  <div className="bg-orange-50 p-4 w-full rounded-xl border border-orange-100 text-center">
                    <div className="text-orange-600">
                      {language === "en"
                        ? "Data Layer (MongoDB, AWS S3)"
                        : language === "zh"
                        ? "数据层 (MongoDB, AWS S3)"
                        : "データレイヤー (MongoDB, AWS S3)"}
                    </div>
                  </div>

                  {/* Connection Line */}
                  <div className="w-0.5 h-6 bg-neutral-300"></div>

                  {/* External Services */}
                  <div className="grid grid-cols-3 gap-6 w-full">
                    <div className="bg-cyan-50 p-4 rounded-xl border border-cyan-100 text-center">
                      <div className="text-cyan-600">
                        {language === "en"
                          ? "Stripe API"
                          : language === "zh"
                          ? "Stripe API"
                          : "Stripe API"}
                      </div>
                    </div>
                    <div className="bg-cyan-50 p-4 rounded-xl border border-cyan-100 text-center">
                      <div className="text-cyan-600">
                        {language === "en"
                          ? "Ethereum Blockchain"
                          : language === "zh"
                          ? "以太坊区块链"
                          : "イーサリアムブロックチェーン"}
                      </div>
                    </div>
                    <div className="bg-cyan-50 p-4 rounded-xl border border-cyan-100 text-center">
                      <div className="text-cyan-600">
                        {language === "en"
                          ? "Shipping APIs"
                          : language === "zh"
                          ? "物流API"
                          : "配送API"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Conclusion Section */}
        <section
          ref={(el) => (sectionRefs.current[3] = el)}
          className="py-20 px-6 bg-neutral-50 relative"
        >
          <div className="container mx-auto max-w-7xl">
            <h2 className="text-3xl md:text-4xl font-light mb-10 text-center text-neutral-800">
              {translations.conclusion[language] || translations.conclusion.en}
            </h2>

            <div className="bg-white p-10 rounded-3xl shadow-sm max-w-4xl mx-auto relative z-10">
              <p className="text-neutral-600 leading-relaxed mb-8">
                {translations.conclusionText[language] ||
                  translations.conclusionText.en}
              </p>

              <div className="mt-10 flex flex-col md:flex-row md:justify-between items-center">
                <div className="mb-6 md:mb-0">
                  <img
                    src="/assets/projects/dhow/cover.jpg"
                    alt="DHOW Logo"
                    className="h-10"
                  />
                </div>
                <div className="flex space-x-4">
                  <a
                    href="https://www.dhow.cn"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-primary-500 text-white px-6 py-2 rounded-full hover:bg-primary-600 transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    {language === "en"
                      ? "Visit Website"
                      : language === "zh"
                      ? "访问网站"
                      : "ウェブサイトを訪問"}
                  </a>
                  <Link
                    to="/projects"
                    className="border border-neutral-200 bg-white px-6 py-2 rounded-full hover:border-primary-300 transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    {language === "en"
                      ? "Back to Projects"
                      : language === "zh"
                      ? "返回项目"
                      : "プロジェクトに戻る"}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Background decoration */}
          <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-primary-100/20 to-transparent z-0"></div>
        </section>
      </div>

      <div className="chain-page project-archive"><Archive project="Dhow" /></div>
      <Footer />
    </div>
  );
};

export default DhowPage;
